import classNames from 'classnames'
import type { FC } from 'react'
import React, { useMemo } from 'react'

import type { ImageProps } from './types'
import { useImageLoaded } from '../../../hooks/use-image-loaded'
import type { PrivateThemeUiProps } from '../../../styles-system/create-component'
import crateComponent from '../../../styles-system/create-component'
import {
  getAriaPrefix,
  getBorder,
  getDataPrefix,
  getLayout,
  getPosition,
  getSpace,
  positions
} from '../../../utils/props-utils'
import Box, { BoxPrivate } from '../box/box'
import Flex from '../flex'
import Icon from '../icon'

const Component = crateComponent('img', 'csg-box')

const ImageBase = React.forwardRef<
  HTMLImageElement,
  ImageProps & PrivateThemeUiProps
>(function ImageBase(props, ref) {
  const { className, __css, ...rest } = props

  const componentStyles = useMemo(
    () => ({
      height: 'auto',
      ...__css
    }),
    [__css]
  )

  return (
    <Component
      ref={ref}
      className={classNames(
        {
          'csg-tui-img': true
        },
        className
      )}
      {...rest}
      __themeKey="images"
      __css={componentStyles}
    />
  )
})

export const ImagePrivate = React.forwardRef<
  HTMLImageElement,
  ImageProps & PrivateThemeUiProps
>(function ImagePrivate(props, ref) {
  const {
    id: idProp,
    fitPosition,
    fit,
    isFixed,
    __css,
    aspectRatio: aspectRatio,
    width,
    src,
    srcSet,
    children: childrenProp,
    missingImagePlaceHolder = 'camera',
    missingImageIcon,
    alt,
    title,
    ...rest
  } = props

  const imageBaseStyles = useMemo(
    () => ({
      width,
      maxWidth: isFixed ? 'unset' : '100%',
      ...(fit ? { objectFit: fit } : {}),
      ...(fitPosition ? { objectPosition: fitPosition } : {}),
      ...(aspectRatio
        ? { position: 'absolute', top: 0, left: 0, width: '100%' }
        : {}),
      ...__css
    }),
    [__css, aspectRatio, fit, fitPosition, isFixed, width]
  )

  // Use a hook instead of onError on the img element to support server-side rendering.
  const imageMeta = useImageLoaded({ src, srcSet })
  const imageFetchFailed = (src || srcSet) && imageMeta.state === 'error'
  const placeHolderIcon = (
    <Box
      className="csg-tui-missing-image-wrapper"
      width={width}
      height="100%"
      relative
      {...{ ...getPosition(rest), ...getLayout(rest), ...positions(rest) }}
    >
      <Flex
        id={idProp}
        className="csg-tui-missing-image"
        alt={alt}
        title={alt}
        justifyContent="center"
        alignItems="center"
        absolute
        top={0}
        left={0}
        bottom={0}
        right={0}
        bg="neutral.light"
        overflow="hidden"
        {...{
          ...getSpace(rest),
          ...getBorder(rest),
          ...getAriaPrefix(rest),
          ...getDataPrefix(rest)
        }}
      >
        {missingImageIcon ? (
          missingImageIcon
        ) : missingImagePlaceHolder === 'camera' ? (
          <Icon icon="dslrCamera" color="neutral.medium" />
        ) : missingImagePlaceHolder === 'shutter' ? (
          <Icon icon="cameraShutter" color="neutral.medium" />
        ) : missingImagePlaceHolder === 'contact' ? (
          <Icon
            icon="mui-person"
            openSize
            color="neutral.medium"
            pt="lg"
            height="100%"
          />
        ) : null}
      </Flex>
    </Box>
  )
  let _children = null
  if (!src || src?.length === 0) {
    _children = placeHolderIcon
  } else if (!imageFetchFailed && !!imageMeta.state) {
    _children = (
      <ImageBase
        id={idProp}
        alt={alt}
        title={title || alt}
        src={src}
        srcSet={srcSet}
        ref={ref}
        {...rest}
        __css={imageBaseStyles}
      />
    )
  } else if (childrenProp != null) {
    _children = childrenProp
  } else {
    _children =
      typeof missingImagePlaceHolder !== 'string'
        ? missingImagePlaceHolder
        : placeHolderIcon
  }

  const boxPrivateStyles = useMemo(
    () => ({
      width: width ? width : '100%',
      height: 0,
      paddingTop:
        typeof aspectRatio === 'number' ? `${aspectRatio * 100}%` : '75%'
    }),
    [aspectRatio, width]
  )

  return aspectRatio ? (
    <BoxPrivate
      className="csg-tui-img-placeholder-aspect-ratio"
      relative
      __css={boxPrivateStyles}
    >
      {_children}
    </BoxPrivate>
  ) : (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>{_children}</>
  )
})

const Image: FC<ImageProps> = React.forwardRef<HTMLImageElement, ImageProps>(
  function Image(props, ref) {
    return <ImagePrivate ref={ref} {...props} />
  }
)

export default Image
